import './client-groups-page.css';
import { addControlStyle } from './client-groups-page-style';

import { useEffect, useState } from 'react';
import { saveAs } from 'file-saver';
import { Button, Checkbox, Form, Input, InputNumber, Modal, notification, Select, Space, Table, Upload } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import { ClientGroup } from '../../model/client-group-model';
import { useNavigate } from 'react-router-dom';
import { Client } from '../../model/client-model';
import { DataMapSourceModel } from '../../model/data-map-source-model';
import { Actions } from '../../util/constant';

import { ClientList } from './client-list';

import {
    useAddClientGroupMutation,
    useGetClientGroupsQuery,
    // useLazyGetClientGroupsQuery,
} from '../../redux/api/client-groups';

import {
    useAddClientMutation,
    useCopyClientMutation,
    useDeleteClientMutation,
    useImportClientMutation,
    useLazyExportClientQuery,
    useLazyGetClientQuery,
    useScheduleClientMutation,
    useUnscheduleClientMutation,
    useUpdateClientMutation
} from '../../redux/api/clients';

import { 
    useLazyGetDataMapSourceQuery, 
    useSaveDataMapSourceMutation 
} from '../../redux/api/data-maps';

import TextArea from 'antd/es/input/TextArea';
import { ClientForm } from '../clients-page/client-form';


const ClientGroupsPage = () => {

    const [ expandedRowKeys, setExpandedRowKeys ] = useState([]);
    const [ client, setClient ] = useState<Client>();
    const [ clientGroup, setClientGroup ] = useState<ClientGroup>();
    const [ isAddClientGroupOpen, setAddClientGroupOpen ] = useState<boolean>(false);
    const [ isAddClientOpen, setAddClientOpen ] = useState<boolean>(false);
    const [ isEditClientOpen, setEditClientOpen ] = useState<boolean>(false);
    const [ isSourceDataOpen, setSourceDataOpen ] = useState<boolean>();
    const [ isScheduleOpen, setScheduleOpen ] = useState<boolean>(false);
    const [ isUnscheduleOpen, setUnscheduleOpen ] = useState<boolean>(false);
    const [ isCopyOpen, setCopyOpen ] = useState<boolean>(false);
    const [ isImportOpen, setImportOpen ] = useState<boolean>(false);
    const [ importFile, setImportFile ] = useState<File | null>(null);
    const [ overwrite, setOverwrite ] = useState<boolean>(false);
    const [ tableHeight, setTableHeight ] = useState(0);
    const [ page, setPage ] = useState<any>({index: 1, pageSize: 25, total: 0});
    
    const [ modal, contextHolder] = Modal.useModal();

    const [ postClientGroup ] = useAddClientGroupMutation();
    const [ getClient ] = useLazyGetClientQuery();
    const [ postClient ] = useAddClientMutation();
    const [ putClient ] = useUpdateClientMutation();
    const [ removeClient ] = useDeleteClientMutation();
    const [ postCopyClient ] = useCopyClientMutation();
    const [ postScheduleClient ] = useScheduleClientMutation();
    const [ postUnscheduleClient ] = useUnscheduleClientMutation();
    const [ getDataMapSource ] = useLazyGetDataMapSourceQuery();
    const [ saveDataMapSource ] = useSaveDataMapSourceMutation();
    const [ postImportClient ] = useImportClientMutation();
    const [ exportClient ] = useLazyExportClientQuery();
    
    const navigate = useNavigate();

    const [newClientGroupForm] = Form.useForm();
    const [newClientForm] = Form.useForm();
    const [sourceDataForm] = Form.useForm();

    const {data, refetch} = useGetClientGroupsQuery(page);
    
    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (name: string) => {
                return name;
            }
        },
        {
            title: '',
            dataIndex: 'action',
            key: 'action',
            width: 240,
            render: (_: any, group: ClientGroup) => {
                return <div style={{ textAlign: 'center', padding: '0 8px' }}>
                    <Space>
                        <Button size="small" onClick={() => onImportClient(group)}>
                            Import Client
                        </Button>
                        <Button size="small" type="primary" onClick={() => onCreateClient(group)}>
                            Add Client
                        </Button>
                    </Space>
                </div>
            }
        },
    ];

    const updateTableHeight = () => {
        const windowHeight = window.innerHeight; // Full screen height
        const calculatedHeight = windowHeight - 288; // Subtract paddings and headers
        setTableHeight(calculatedHeight);
    };

    useEffect(() => {

        updateTableHeight(); // Set height on mount
        window.addEventListener("resize", updateTableHeight); // Adjust height on resize
        return () => window.removeEventListener("resize", updateTableHeight);

    }, []);

    useEffect(() => {
        refetch();
    }, [page.index]);

    const onCreateClientGroup = () => {
        setAddClientGroupOpen(true);
    }

    const onCreateClient = (group: ClientGroup) => {
        setClientGroup(group);
        setAddClientOpen(true);
    }

    const onImportClient = (group: ClientGroup) => {
        setClientGroup(group);
        setImportOpen(true);
    }

    const handleImportClient = async () => {
        if (!importFile) return;
        try {
            const result: any = await postImportClient({
                file: importFile,
                overwrite,
                clientGroupId: clientGroup?.id
            }).unwrap();
            refetch();
            setImportOpen(false);
            setImportFile(null);
            setOverwrite(false);
            notification.success({
                message: 'Client Imported!',
                description: `${result.name} has been imported into ${clientGroup?.name}.`
            });
        } catch (e: any) {
            notification.error({
                message: 'Import Failed!',
                description: e?.data ?? 'An error occurred while importing the client.'
            });
        }
    };

    const addClientGroup = async (clientGroup: ClientGroup): Promise<void> => {

        try {

            console.log('adding client group', clientGroup);
            const response = await postClientGroup(clientGroup).unwrap();
            
            refetch();

            setAddClientGroupOpen(false);

            notification.success({
                message: "Client Created!",
                description: `The client ${clientGroup.name} has been created.`
            });
        } catch (e) {
            console.log(e);
            notification.error({
                message: "Creating Client Failed!",
                description: `An error occurred while creating the client`
            });
        }
    }
    

    /**
     * Adds a client by sending the client data to the server.
     * 
     * @param client the client to add
     */
    const addClient = async (client: Client): Promise<void> => {

        try {

            const sourceData = client.sourceData;
            delete client.sourceData;
            const clientWithGroupId = { ...client, clientGroupId: clientGroup?.id };
            const response = await postClient(clientWithGroupId).unwrap();
            
            await saveDataMapSource({clientId: response.id, source: sourceData});
            refetch();

            setAddClientOpen(false);

            notification.success({
                message: "Client Created!",
                description: `The client ${client.name} has been created.`
            });
        } catch (e) {
            console.log(e);
            notification.error({
                message: "Creating Client Failed!",
                description: `An error occurred while creating the client`
            });
        }
    }

    const updateClient = async (client: Client): Promise<void> => {

        try {

            const sourceData = client.sourceData;
            delete client.sourceData;
            const response = await putClient({id: client.id, payload: client}).unwrap();
            
            await saveDataMapSource({clientId: client.id, source: sourceData});
            
            refetch();

            setEditClientOpen(false);

            notification.success({
                message: "Client Update!",
                description: `The client ${client.name} has been update.`
            });
        } catch (e) {
            console.log(e);
            notification.error({
                message: "Updating Client Failed!",
                description: `An error occurred while creating the client`
            });
        }
    }

    const deleteClient = async (client: Client): Promise<void> => {

        try {
            
            const response = await removeClient(client).unwrap();

            refetch();
            
            notification.success({
                message: "Client Deleted!",
                description: `The client ${client.name} has been deleted.`
            });

        } catch (e) {
            console.log(e);
            notification.error({
                message: "Deleting Client Failed!",
                description: `An error occurred while deleting the client.`
            });
        }
    }

    /**
     * Opens the source data modal.
     */
    const openSourceData = async (client: Client) => {
        
        const dataMapSource: DataMapSourceModel = await getDataMapSource(client.id).unwrap();
        
        sourceDataForm.setFieldValue('clientId',client.id);
        sourceDataForm.setFieldValue('source', dataMapSource?.source);
        
        setSourceDataOpen(true);
    }

    /**
     * Opens the script editor page.
     */
    const openScriptEditorPage = (client: Client) => {
        navigate(`/script-editor/${client?.id}`);
    }

    /**
     * Opens the client's data mapping page.
     */
    const openDataMappingPage = (client: Client) => {
        navigate(`/data-mapping/${client?.id}`);
    }
    
    /**
     * Opens the pdf bulder page.
     */
    const openPdfBuilderPage = (client: Client) => {
        navigate(`/pdf-builder/${client?.id}`);
    }

    const saveSourceData = async(value: any) => {
        await saveDataMapSource({clientId: value.clientId, source: value.source});
        setSourceDataOpen(false);
    }

    /**
     * Schedules a client for fetching data and sending to target client.
     */
    const scheduleClient = async () => {

        try {

            const response = await postScheduleClient(client?.id).unwrap();
            
            refetch();
            
            notification.success({
                message: "Client Scheduled!",
                description: `The client ${client?.name} has been scheduled for dispatching.`
            });

        } catch (e: any) {
            console.log(e);
            notification.error({
                message: "Scheduling Client Failed!",
                description: e.data ? e.data : `An error occurred while scheduling the client`
            });
        }
        setScheduleOpen(false);
    }

    /**
     * Cancels the scheduled client from fetching data and sending to target client.
     */
    const unscheduleClient = async () => {

        try {

            const response = await postUnscheduleClient(client?.id).unwrap();
            
            refetch();

            setUnscheduleOpen(false);

            notification.success({
                message: "Client Schedule Canceled!",
                description: `The schedule of client ${client?.name} has been canceled.`
            });
        } catch (e) {
            console.log(e);
            notification.error({
                message: "Cancel Client Schedule Failed!",
                description: `An error occurred while cancelling the client's schedule. ${e}`
            });
        }
    }

    const copyClient = async (value: any) => {

        try {
            
            const response = await postCopyClient({
                clientId: client?.id, 
                clientCode: value.clientCode, 
                name: value.name
            }).unwrap();

            refetch();

            setCopyOpen(false);

            notification.success({
                message: "Client Copie!",
                description: `The operation to copy a client is successful.`
            });
            } catch (e) {
            console.log(e);
            notification.error({
                message: "Cancel Client Schedule Failed!",
                description: `An error occurred while copying the client. ${e}`
            });
        }
    }

    /**
     * Cancels the schedule client confirmation modal.
     */
    const closeScheduleModal = (): void => {
        setScheduleOpen(false);
    }

    /**
     * Cancels the client schedule canceling confirmation modal.
     */
    const closeUnscheduleModal = (): void => {
        setUnscheduleOpen(false);
    }

    /**
     * Cancels the create client copy modal.
     */
    const closeCopyModal = (): void => {
        setCopyOpen(false);
    }

    const onEditOpen = async (dto: Client) => {

        const client: Client = await getClient(dto.id).unwrap();
        const source: DataMapSourceModel = await getDataMapSource(client.id).unwrap();
        const clientWithSourceData = {...client, sourceData: source?.source};
        setClient(clientWithSourceData);
        setEditClientOpen(true);
    }

    const onDelete = (client: Client) => {
        
        modal.confirm({
            title: 'Delete the client',
            icon: <ExclamationCircleOutlined />,
            content: 'Are you sure to delete this client?',
            okText: 'Ok',
            cancelText: 'Cancel',
            onOk: () => deleteClient(client),
        });
    }

    /**
     * Handles the click action on the action button of the client
     * in the client list (the 3-dot vertical button on the right-most
     * column)
     * 
     * @param key the key of the menu item that was clicked
     * @param client the client that was clicked
     */
    const handleExportClient = async (client: Client) => {
        try {
            const blob = await exportClient(client.id).unwrap();
            saveAs(blob as Blob, `${client.clientCode}-export.json`);
            notification.success({
                message: 'Client Exported!',
                description: `${client.name} has been exported successfully.`
            });
        } catch (e) {
            notification.error({
                message: 'Export Failed!',
                description: 'An error occurred while exporting the client.'
            });
        }
    };

    const handleClientAction = (key: string, client: Client) => {

        setClient(client);

        switch (key) {
            case Actions.UPDATE: onEditOpen(client); break;
            case Actions.DELETE: onDelete(client); break;
            case Actions.SOURCE: openSourceData(client); break;
            case Actions.SCRIPT: openScriptEditorPage(client); break;
            case Actions.DATA_MAPPING: openDataMappingPage(client); break;
            case Actions.PDF_BUILDER: openPdfBuilderPage(client); break;
            case Actions.SCHEDULE: setScheduleOpen(true); break;
            case Actions.UNSCHEDULE: setUnscheduleOpen(true); break;
            case Actions.COPY: setCopyOpen(true); break;
            case Actions.EXPORT: handleExportClient(client); break;
        }
    }

    // Handle page change
    const handleTableChange = (page: any) => {
        setPage((prev: any) => ({ ...prev, current: page.index }));
    }

    return <>
        {contextHolder}
        <div style={addControlStyle}>
            <h1>Client Groups</h1>
            <div>
                <Button
                    type="primary"
                    onClick={onCreateClientGroup}>
                    Create Client Group
                </Button>
            </div>

        </div>

        <Table
            className="client-groups-table"
            rowKey="id"
            columns={columns}
            dataSource={data?.items}
            expandable={{
                expandedRowRender: (record) => (
                    <ClientList 
                        clients={record.clients || []} 
                        handleClientAction={handleClientAction}
                        />
                ),
                rowExpandable: (record) => true,
            }}
            scroll={{ y: tableHeight }}
            pagination={{
                current: page.index,
                pageSize: page.pageSize,
                total: page.total
            }}/>
        
        <Modal
            title={'Create Client Group'}
            closable={false}
            open={isAddClientGroupOpen}
            onOk={() => newClientGroupForm.submit()}
            onCancel={() => setAddClientGroupOpen(false)}
            width={400}
            destroyOnClose={true}
        >
            <Form 
                form={newClientGroupForm}
                onFinish={(addClientGroup)}>
                <label>Name *</label>
                <Form.Item
                    name="name"
                    rules={[{required: true, message: 'Name is required.'}]}>
                    <Input />
                </Form.Item>
            </Form>
        </Modal>

        <Modal
            closable={false}
            open={isAddClientOpen}
            width={600}
            footer={null}
            destroyOnClose={true}>
            <ClientForm 
                onClose={() => setAddClientOpen(false)}
                addClient={addClient}
            />
        </Modal>

        <Modal
            title={'Client Information'}
            closable={false}
            open={isEditClientOpen}
            width={600}
            footer={null}
            destroyOnClose={true}
        >
            <ClientForm 
                client={client}
                onClose={() => setEditClientOpen(false)}
                updateClient={updateClient}
            />
        </Modal>

        <Modal
            title={'Set Source Data'}
            closable={false}
            open={isSourceDataOpen}
            onOk={() => sourceDataForm.submit()}
            onCancel={() => setSourceDataOpen(false)}
            width={600}
            destroyOnClose={true}
        >
            <Form 
                form={sourceDataForm}
                onFinish={saveSourceData}
                >
                <Form.Item name="clientId">
                    <Input style={{display: 'none'}} />
                </Form.Item>

                <label>Source Data *</label>
                <Form.Item
                    name="source"
                    rules={[{required: true, message: 'Source Data is is required.'}]}
                >
                    <TextArea style={{width: '100%', height: 600}} />
                </Form.Item>
            </Form>
        </Modal>

        <Modal
            title={'Schedule Client'}
            closable={false}
            open={isScheduleOpen}
            onOk={scheduleClient}
            onCancel={closeScheduleModal}
            width={600}
        >
            <p>Are you sure you want to schedule the client <span style={{ color: 'green' }}>[{client?.name}]</span> for dispatching?</p>
        </Modal>

        <Modal
            title={'Cancel Client Schedule'}
            closable={false}
            open={isUnscheduleOpen}
            onOk={unscheduleClient}
            onCancel={closeUnscheduleModal}
            width={600}
        >
            <p>Are you sure you want to cancel the schedule for client <span style={{ color: 'green' }}>[{client?.name}]</span>?</p>
        </Modal>

        <Modal
            title={'Create Copy'}
            closable={false}
            open={isCopyOpen}
            onOk={() => newClientForm.submit()}
            onCancel={closeCopyModal}
            width={400}
            destroyOnClose={true}
        >
            <Form
                form={newClientForm}
                onFinish={copyClient}>
                <label>Name *</label>
                <Form.Item
                    name="name"
                    rules={[{required: true, message: 'Name is required.'}]}>
                    <Input />
                </Form.Item>

                <label>Client Code *</label>
                <Form.Item
                    name="clientCode"
                    rules={[{required: true, message: 'Client code is required.'}]}>
                    <Input />
                </Form.Item>
            </Form>
        </Modal>

        <Modal
            title={`Import Client into ${clientGroup?.name}`}
            closable={false}
            open={isImportOpen}
            onOk={handleImportClient}
            okText="Import"
            okButtonProps={{ disabled: !importFile }}
            onCancel={() => { setImportOpen(false); setImportFile(null); setOverwrite(false); }}
            width={400}
            destroyOnClose={true}
        >
            <Upload
                accept=".json"
                maxCount={1}
                beforeUpload={(file) => { setImportFile(file); return false; }}
                onRemove={() => setImportFile(null)}
            >
                <Button>Select Export File (.json)</Button>
            </Upload>
            <div style={{ marginTop: 16 }}>
                <Checkbox
                    checked={overwrite}
                    onChange={(e) => setOverwrite(e.target.checked)}
                >
                    Overwrite if client code already exists
                </Checkbox>
            </div>
        </Modal>
    </>
};

export default ClientGroupsPage;